import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { AlertTriangle, Archive, Clock, Download, HardDrive, RefreshCw, RotateCcw, Send, ShieldCheck, Trash2 } from 'lucide-react';

const defaultSettings = {
  enabled: false,
  interval_hours: 24,
  keep_last: 7,
  include_uploads: true,
  telegram_enabled: false,
  telegram_use_env: true,
  telegram_bot_token: '',
  telegram_chat_id: '',
  telegram_bot_token_masked: '',
  telegram_chat_id_masked: '',
  telegram_bot_token_configured: false,
  telegram_chat_id_configured: false,
  telegram_env_bot_configured: false,
  telegram_env_chat_configured: false,
  telegram_env_ready: false,
  telegram_config_source: 'none',
  telegram_config_ready: false,
  telegram_last_sent_at: null,
  telegram_last_sent_backup_name: null,
  telegram_last_status: null,
  telegram_last_error: null,
  last_project_backup_at: null,
  last_project_backup_name: null,
  last_project_backup_status: null,
  last_project_backup_error: null,
  last_restore_at: null,
  last_restore_backup_name: null,
  last_restore_rollback_name: null,
  last_restore_status: null,
  last_restore_error: null,
  last_run_at: null,
  last_backup_name: null,
  last_status: null,
  last_error: null,
  next_run_at: null,
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('id-ID');
};

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let index = 0;
  while (size >= 1024 && index < units.length - 1) {
    size /= 1024;
    index += 1;
  }
  return `${size.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const formatTelegramStatus = (value) => {
  if (value === 'sent') return 'Terkirim';
  if (value === 'failed') return 'Gagal';
  if (value === 'skipped') return 'Dilewati';
  if (value === 'disabled') return 'Nonaktif';
  return '-';
};

const formatRestoreStatus = (value) => {
  if (value === 'success') return 'Berhasil';
  if (value === 'failed') return 'Gagal';
  return '-';
};

const formatBackupKind = (value) => {
  if (value === 'project') return 'Project';
  return 'Data';
};

const BackupManager = () => {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState(defaultSettings);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningBackup, setRunningBackup] = useState(false);
  const [runningProjectBackup, setRunningProjectBackup] = useState(false);
  const [restoringBackupName, setRestoringBackupName] = useState('');

  const fetchData = async () => {
    try {
      const res = await api.get('/admin/backups');
      setSettings({
        ...defaultSettings,
        ...(res.data.settings || {}),
        telegram_bot_token: '',
        telegram_chat_id: '',
      });
      setBackups(res.data.items || []);
    } catch (error) {
      toast.error('Gagal memuat data backup');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!settings.enabled,
        interval_hours: Number(settings.interval_hours) || 24,
        keep_last: Number(settings.keep_last) || 7,
        include_uploads: !!settings.include_uploads,
        telegram_enabled: !!settings.telegram_enabled,
        telegram_use_env: !!settings.telegram_use_env,
        telegram_bot_token: settings.telegram_bot_token || '',
        telegram_chat_id: settings.telegram_chat_id || '',
      };
      const res = await api.put('/admin/backup-settings', payload);
      setSettings({
        ...defaultSettings,
        ...(res.data.settings || {}),
        telegram_bot_token: '',
        telegram_chat_id: '',
      });
      toast.success(res.data.message || 'Pengaturan backup disimpan');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan pengaturan backup');
    } finally {
      setSaving(false);
    }
  };

  const handleRunBackup = async () => {
    setRunningBackup(true);
    try {
      const res = await api.post('/admin/backups/run');
      const telegramDelivery = res.data.backup?.telegram_delivery;
      toast.success(`Backup berhasil dibuat: ${res.data.backup?.name || 'arsip baru'}`);
      if (telegramDelivery?.status === 'sent') {
        toast.success('File backup juga berhasil dikirim ke Telegram');
      } else if (telegramDelivery?.status === 'failed') {
        toast.error(telegramDelivery.message || 'Backup selesai, tetapi gagal dikirim ke Telegram');
      } else if (telegramDelivery?.status === 'skipped' && settings.telegram_enabled) {
        toast.error(telegramDelivery.message || 'Backup selesai, tetapi Telegram dilewati');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menjalankan backup manual');
    } finally {
      setRunningBackup(false);
    }
  };

  const handleRunProjectBackup = async () => {
    setRunningProjectBackup(true);
    try {
      const res = await api.post('/admin/backups/run-project');
      const telegramDelivery = res.data.backup?.telegram_delivery;
      toast.success(`Backup project berhasil dibuat: ${res.data.backup?.name || 'arsip project baru'}`);
      if (telegramDelivery?.status === 'sent') {
        toast.success('Backup project juga berhasil dikirim ke Telegram');
      } else if (telegramDelivery?.status === 'failed') {
        toast.error(telegramDelivery.message || 'Backup project selesai, tetapi gagal dikirim ke Telegram');
      } else if (telegramDelivery?.status === 'skipped' && settings.telegram_enabled) {
        toast.error(telegramDelivery.message || 'Backup project selesai, tetapi Telegram dilewati');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menjalankan backup project');
    } finally {
      setRunningProjectBackup(false);
    }
  };

  const handleDownload = async (backupName) => {
    try {
      const res = await api.get(`/admin/backups/${encodeURIComponent(backupName)}/download`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = backupName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Gagal mendownload file backup');
    }
  };

  const handleDelete = async (backupName) => {
    if (!window.confirm(`Hapus file backup ${backupName}?`)) {
      return;
    }
    try {
      const res = await api.delete(`/admin/backups/${encodeURIComponent(backupName)}`);
      toast.success(res.data.message || 'File backup dihapus');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus file backup');
    }
  };

  const handleRestore = async (backupName) => {
    const confirmed = window.confirm(
      `Restore data dari ${backupName}?\n\n` +
      'Semua data aktif akan ditimpa oleh isi backup ini. ' +
      'Sistem akan membuat backup rollback otomatis sebelum restore dijalankan.'
    );
    if (!confirmed) {
      return;
    }

    setRestoringBackupName(backupName);
    try {
      const res = await api.post(`/admin/backups/${encodeURIComponent(backupName)}/restore`);
      setSettings({ ...defaultSettings, ...(res.data.settings || {}) });
      setBackups(res.data.items || []);
      toast.success(res.data.message || 'Restore berhasil dijalankan');
      toast.success(`Backup rollback dibuat: ${res.data.restore?.rollback_backup?.name || '-'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal melakukan restore data');
    } finally {
      setRestoringBackupName('');
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Akses ditolak. Hanya admin yang bisa mengakses halaman ini.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="backup-manager-page">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Backup Center</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Kelola auto backup data, backup project penuh manual, restore, dan pengiriman ke Telegram.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={handleRunBackup} disabled={runningBackup || loading} data-testid="run-backup-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${runningBackup ? 'animate-spin' : ''}`} />
            {runningBackup ? 'Backup Data...' : 'Backup Data'}
          </Button>
          <Button variant="secondary" onClick={handleRunProjectBackup} disabled={runningProjectBackup || loading} data-testid="run-project-backup-btn">
            <Archive className={`h-4 w-4 mr-2 ${runningProjectBackup ? 'animate-spin' : ''}`} />
            {runningProjectBackup ? 'Backup Project...' : 'Backup Project'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              <div>
                <p className="text-xs font-semibold uppercase text-emerald-700">Status</p>
                <p className="text-2xl font-black text-emerald-600">
                  {settings.enabled ? 'Aktif' : 'Nonaktif'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-xs font-semibold uppercase text-blue-700">Backup Berikutnya</p>
                <p className="text-sm font-bold text-blue-600">
                  {settings.next_run_at ? formatDate(settings.next_run_at) : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-violet-50 border-violet-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Archive className="h-5 w-5 text-violet-600" />
              <div>
                <p className="text-xs font-semibold uppercase text-violet-700">Total Arsip</p>
                <p className="text-2xl font-black text-violet-600">{backups.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-100 border-zinc-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <HardDrive className="h-5 w-5 text-zinc-700" />
              <div>
                <p className="text-xs font-semibold uppercase text-zinc-700">Backup Terakhir</p>
                <p className="text-sm font-bold text-zinc-700">
                  {settings.last_run_at ? formatDate(settings.last_run_at) : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border border-border/60">
        <CardHeader>
          <CardTitle>Pengaturan Auto Backup</CardTitle>
          <CardDescription>
            Auto backup ini khusus untuk data aplikasi dan file upload. Backup project penuh dijalankan manual.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="backup-enabled"
              checked={settings.enabled}
              onCheckedChange={(checked) => handleSettingChange('enabled', !!checked)}
            />
            <Label htmlFor="backup-enabled" className="cursor-pointer">
              Aktifkan auto backup
            </Label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Interval Backup (jam)</Label>
              <Input
                type="number"
                min="1"
                max="720"
                value={settings.interval_hours}
                onChange={(e) => handleSettingChange('interval_hours', e.target.value)}
                data-testid="backup-interval-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Simpan Maksimal Arsip</Label>
              <Input
                type="number"
                min="1"
                max="90"
                value={settings.keep_last}
                onChange={(e) => handleSettingChange('keep_last', e.target.value)}
                data-testid="backup-keep-last-input"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="backup-uploads"
              checked={settings.include_uploads}
              onCheckedChange={(checked) => handleSettingChange('include_uploads', !!checked)}
            />
            <Label htmlFor="backup-uploads" className="cursor-pointer">
              Sertakan file upload/logo ke dalam backup
            </Label>
          </div>

          <div className="pt-2 border-t space-y-5">
            <div className="flex items-start gap-3">
              <Send className="h-5 w-5 mt-0.5 text-sky-600" />
              <div>
                <h3 className="font-semibold">Kirim Backup ke Telegram</h3>
                <p className="text-sm text-muted-foreground">
                  Setiap backup manual atau otomatis akan langsung dikirim ke chat admin Telegram.
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="telegram-enabled"
                checked={settings.telegram_enabled}
                onCheckedChange={(checked) => handleSettingChange('telegram_enabled', !!checked)}
              />
              <Label htmlFor="telegram-enabled" className="cursor-pointer">
                Aktifkan kirim backup ke Telegram
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="telegram-use-env"
                checked={settings.telegram_use_env}
                onCheckedChange={(checked) => handleSettingChange('telegram_use_env', !!checked)}
              />
              <Label htmlFor="telegram-use-env" className="cursor-pointer">
                Ambil bot token dan chat ID dari file `.env`
              </Label>
            </div>

            <div className="rounded-lg border bg-sky-50/70 border-sky-200 p-4 text-sm text-sky-900 space-y-1">
              <p className="font-semibold">Mode konfigurasi Telegram</p>
              <p>
                Sumber aktif: <span className="font-medium">{settings.telegram_config_source === 'env' ? '.env' : settings.telegram_config_source === 'dashboard' ? 'Dashboard Admin' : 'Belum siap'}</span>
              </p>
              <p>
                Status `.env`: bot token {settings.telegram_env_bot_configured ? 'terisi' : 'kosong'}, chat ID {settings.telegram_env_chat_configured ? 'terisi' : 'kosong'}
              </p>
              <p className="text-sky-800/80">
                Jika pakai `.env`, isi `TELEGRAM_BOT_TOKEN` dan `TELEGRAM_CHAT_ID` pada file env backend.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bot Token Telegram</Label>
                <Input
                  type="password"
                  value={settings.telegram_bot_token || ''}
                  onChange={(e) => handleSettingChange('telegram_bot_token', e.target.value)}
                  disabled={settings.telegram_use_env}
                  placeholder={settings.telegram_use_env ? 'Menggunakan TELEGRAM_BOT_TOKEN dari .env' : '123456789:AA...'}
                />
                <p className="text-xs text-muted-foreground">
                  {settings.telegram_bot_token_configured
                    ? `Token tersimpan: ${settings.telegram_bot_token_masked || 'tersimpan'}`
                    : 'Belum ada token dashboard yang tersimpan'}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Chat ID Admin</Label>
                <Input
                  value={settings.telegram_chat_id || ''}
                  onChange={(e) => handleSettingChange('telegram_chat_id', e.target.value)}
                  disabled={settings.telegram_use_env}
                  placeholder={settings.telegram_use_env ? 'Menggunakan TELEGRAM_CHAT_ID dari .env' : 'Isi chat ID tujuan'}
                />
                <p className="text-xs text-muted-foreground">
                  {settings.telegram_chat_id_configured
                    ? `Chat ID tersimpan: ${settings.telegram_chat_id_masked || 'tersimpan'}`
                    : 'Belum ada chat ID dashboard yang tersimpan'}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Kosongkan input token/chat ID jika tidak ingin mengganti secret yang sudah tersimpan.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t">
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Status terakhir</p>
              <p className="font-medium mt-1">
                {settings.last_status === 'success' ? 'Berhasil' : settings.last_status === 'failed' ? 'Gagal' : '-'}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Nama file terakhir</p>
              <p className="font-medium mt-1 break-all">{settings.last_backup_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Error terakhir</p>
              <p className="font-medium mt-1 break-words">{settings.last_error || '-'}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-2 border-t">
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Telegram terakhir</p>
              <p className="font-medium mt-1">{formatTelegramStatus(settings.telegram_last_status)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Terkirim pada</p>
              <p className="font-medium mt-1">{formatDate(settings.telegram_last_sent_at)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">File Telegram terakhir</p>
              <p className="font-medium mt-1 break-all">{settings.telegram_last_sent_backup_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Error Telegram</p>
              <p className="font-medium mt-1 break-words">{settings.telegram_last_error || '-'}</p>
            </div>
          </div>

          <div>
            <Button onClick={handleSave} disabled={saving} data-testid="save-backup-settings-btn">
              {saving ? 'Menyimpan...' : 'Simpan Pengaturan'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border border-border/60">
        <CardHeader>
          <CardTitle>Backup Project Manual</CardTitle>
          <CardDescription>
            Membuat arsip project penuh berisi source code, file env, build frontend, uploads, dan file deploy penting.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Status terakhir</p>
              <p className="font-medium mt-1">{formatRestoreStatus(settings.last_project_backup_status)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Waktu backup</p>
              <p className="font-medium mt-1">{formatDate(settings.last_project_backup_at)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">File terakhir</p>
              <p className="font-medium mt-1 break-all">{settings.last_project_backup_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Error terakhir</p>
              <p className="font-medium mt-1 break-words">{settings.last_project_backup_error || '-'}</p>
            </div>
          </div>
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            Backup project penuh hanya tersedia lewat trigger manual agar arsip kode tidak ikut dijadwalkan otomatis.
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border border-border/60">
        <CardHeader>
          <CardTitle>Arsip Backup</CardTitle>
          <CardDescription>Daftar file backup yang tersedia untuk diunduh, direstore, atau dibersihkan.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="mx-6 mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 mt-0.5 text-amber-600" />
              <div className="space-y-1">
                <p className="font-semibold">Restore akan menimpa data aktif</p>
                <p>
                  Sebelum restore dijalankan, sistem otomatis membuat backup rollback agar data sekarang tetap bisa
                  dikembalikan jika diperlukan.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 px-6 pt-6">
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Restore terakhir</p>
              <p className="font-medium mt-1">{formatRestoreStatus(settings.last_restore_status)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Waktu restore</p>
              <p className="font-medium mt-1">{formatDate(settings.last_restore_at)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Backup sumber</p>
              <p className="font-medium mt-1 break-all">{settings.last_restore_backup_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Backup rollback</p>
              <p className="font-medium mt-1 break-all">{settings.last_restore_rollback_name || '-'}</p>
            </div>
          </div>

          <div className="px-6 pt-4 pb-2">
            <p className="text-xs font-semibold uppercase text-muted-foreground">Error restore terakhir</p>
            <p className="font-medium mt-1 break-words">{settings.last_restore_error || '-'}</p>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead>Nama File</TableHead>
                  <TableHead>Jenis</TableHead>
                  <TableHead>Dibuat</TableHead>
                  <TableHead>Ukuran</TableHead>
                  <TableHead className="text-center w-[260px]">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      Memuat...
                    </TableCell>
                  </TableRow>
                ) : backups.length > 0 ? (
                  backups.map((item) => (
                    <TableRow key={item.name} className="hover:bg-zinc-50/50">
                      <TableCell className="font-medium break-all">{item.name}</TableCell>
                      <TableCell>
                        <span className={`rounded px-2 py-1 text-xs ${item.backup_kind === 'project' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          {formatBackupKind(item.backup_kind)}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(item.created_at)}</TableCell>
                      <TableCell>{formatBytes(item.size_bytes)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1 flex-wrap">
                          <Button variant="ghost" size="icon" onClick={() => handleDownload(item.name)} data-testid={`download-backup-${item.name}`}>
                            <Download className="h-4 w-4 text-blue-600" />
                          </Button>
                          {item.backup_kind !== 'project' && (
                            <Button
                              variant="ghost"
                              onClick={() => handleRestore(item.name)}
                              disabled={restoringBackupName === item.name}
                              className="text-amber-700 hover:text-amber-800"
                              data-testid={`restore-backup-${item.name}`}
                            >
                              <RotateCcw className={`h-4 w-4 mr-2 ${restoringBackupName === item.name ? 'animate-spin' : ''}`} />
                              {restoringBackupName === item.name ? 'Restore...' : 'Restore'}
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(item.name)} data-testid={`delete-backup-${item.name}`}>
                            <Trash2 className="h-4 w-4 text-rose-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      Belum ada file backup
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BackupManager;
